import { useState } from 'react'
import { CheckCircle2, ChevronDown, ChevronRight, User } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import type { MemberPrepSession } from '../../redux/types/charterTypes'
import {
  formatProfileKey as formatKey,
  renderProfileValue as renderValue,
} from '../../utils/profileFormat'

// ── Own profile modal (mirrors PrepProfileModal) ──────────────────────────────

interface OwnProfileModalProps {
  isOpen: boolean
  onClose: () => void
  profile: MemberPrepSession
}

function OwnProfileModal({ isOpen, onClose, profile }: OwnProfileModalProps) {
  const structured = profile.structured_profile
  if (!structured) return null

  const individualEntries = Object.entries(structured.individual_content ?? {})
  const negotiationEntries = Object.entries(structured.negotiation_positions ?? {})

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-lg p-0 gap-0 overflow-hidden'>
        <DialogHeader className='px-6 pt-6 pb-4'>
          <div className='flex items-center justify-between'>
            <DialogTitle className='text-base font-semibold text-slate-900'>
              Your Prep Profile
            </DialogTitle>
            <Badge
              variant='outline'
              className={
                profile.profile_approved
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }
            >
              {profile.profile_approved ? 'Approved' : 'Draft'}
            </Badge>
          </div>
        </DialogHeader>

        <ScrollArea className='max-h-[70vh] px-6 pb-6'>
          <div className='space-y-3'>
            {/* Shareable summary */}
            {structured.shareable_summary && (
              <div className='bg-indigo-50 border border-indigo-100 rounded-lg p-3'>
                <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1'>
                  Shared Summary
                </p>
                <p className='text-xs text-indigo-800 leading-relaxed'>
                  {structured.shareable_summary}
                </p>
              </div>
            )}

            {/* Individual content sections */}
            {individualEntries.length > 0 && (
              <>
                <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400'>
                  Individual Profile
                </p>
                {individualEntries.map(([key, value]) => (
                  <div key={key}>
                    <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5'>
                      {formatKey(key)}
                    </p>
                    <p className='text-xs text-slate-700 leading-relaxed'>
                      {renderValue(value)}
                    </p>
                    <Separator className='mt-2' />
                  </div>
                ))}
              </>
            )}

            {/* Negotiation positions */}
            {negotiationEntries.length > 0 && (
              <>
                <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mt-2'>
                  Negotiation Positions
                </p>
                {negotiationEntries.map(([key, value]) => (
                  <div key={key}>
                    <p className='text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5'>
                      {formatKey(key)}
                    </p>
                    <p className='text-xs text-slate-700 leading-relaxed'>
                      {renderValue(value)}
                    </p>
                    <Separator className='mt-2' />
                  </div>
                ))}
              </>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

// ── Teammate summary card ─────────────────────────────────────────────────────

interface TeammateCardProps {
  member: MemberPrepSession
}

function TeammateCard({ member }: TeammateCardProps) {
  const [expanded, setExpanded] = useState(false)
  const summary = member.shareable_summary

  return (
    <div className='bg-white border border-slate-100 rounded-lg overflow-hidden'>
      <button
        onClick={() => setExpanded((v) => !v)}
        className='w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors text-left'
      >
        <div className='flex items-center gap-2 min-w-0'>
          <div className='flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center'>
            <User className='h-3 w-3 text-indigo-500' />
          </div>
          <span className='text-xs font-medium text-slate-700 truncate'>
            {member.member_name}
          </span>
          {member.profile_approved && (
            <CheckCircle2 className='h-3 w-3 text-emerald-500 flex-shrink-0' />
          )}
        </div>
        {expanded ? (
          <ChevronDown className='h-3.5 w-3.5 text-slate-400 flex-shrink-0' />
        ) : (
          <ChevronRight className='h-3.5 w-3.5 text-slate-400 flex-shrink-0' />
        )}
      </button>

      {expanded && (
        <div className='px-3 pb-3 pt-1 border-t border-slate-50'>
          {summary ? (
            <p className='text-[11px] text-slate-600 leading-relaxed'>{summary}</p>
          ) : (
            <p className='text-[11px] text-slate-400 italic'>No summary shared</p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Own profile card (sidebar button) ────────────────────────────────────────

interface OwnProfileCardProps {
  profile: MemberPrepSession
  onClick: () => void
}

function OwnProfileCard({ profile, onClick }: OwnProfileCardProps) {
  const structured = profile.structured_profile
  const sectionCount = structured
    ? Object.keys(structured.individual_content ?? {}).length +
      Object.keys(structured.negotiation_positions ?? {}).length
    : 0

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left bg-white border border-slate-100 rounded-lg p-3 shadow-sm',
        'hover:border-indigo-200 hover:shadow-md transition-all group'
      )}
    >
      <div className='flex items-center justify-between mb-1.5'>
        <div className='flex items-center gap-1.5'>
          {profile.profile_approved ? (
            <CheckCircle2 className='h-3.5 w-3.5 text-emerald-500 flex-shrink-0' />
          ) : (
            <User className='h-3.5 w-3.5 text-amber-500 flex-shrink-0' />
          )}
          <span className='text-xs font-semibold text-slate-700'>Your Profile</span>
        </div>
        <div className='flex items-center gap-1.5'>
          <Badge
            variant='outline'
            className={cn(
              'text-[10px] px-1.5 py-0 h-4 font-medium',
              profile.profile_approved
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            )}
          >
            {profile.profile_approved ? 'Approved' : 'Draft'}
          </Badge>
          <ChevronRight className='h-3 w-3 text-slate-400 group-hover:text-indigo-500 transition-colors' />
        </div>
      </div>

      <div className='flex items-center gap-2'>
        <span className='text-[10px] text-slate-400'>{sectionCount} sections</span>
        {structured?.shareable_summary && (
          <>
            <span className='text-[10px] text-slate-300'>·</span>
            <p className='text-[10px] text-slate-500 line-clamp-1 leading-relaxed'>
              {structured.shareable_summary}
            </p>
          </>
        )}
      </div>
    </button>
  )
}

// ── Main export ───────────────────────────────────────────────────────────────

interface TeamProfilesPanelProps {
  myProfile: MemberPrepSession | null
  teamProfiles: MemberPrepSession[]
}

export function TeamProfilesPanel({
  myProfile,
  teamProfiles,
}: TeamProfilesPanelProps) {
  const [modalOpen, setModalOpen] = useState(false)

  // Backend already excludes the requesting user via MyTeamSummariesView queryset
  const teammates = teamProfiles

  if (!myProfile && teammates.length === 0) return null

  return (
    <div className='space-y-2'>
      {/* Own profile */}
      {myProfile?.structured_profile && (
        <>
          <OwnProfileCard profile={myProfile} onClick={() => setModalOpen(true)} />
          <OwnProfileModal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            profile={myProfile}
          />
        </>
      )}

      {/* Teammates */}
      {teammates.length > 0 && (
        <div className='space-y-1.5'>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1'>
            Team Profiles
          </p>
          {teammates.map((member) => (
            <TeammateCard key={member.id} member={member} />
          ))}
        </div>
      )}
    </div>
  )
}
